import { getChannelName, isService } from '~publish/helpers/channels'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import {
  canDiscloseAiContent,
  getIsAiGenerated,
} from '~publish/legacy/composer/composer/components/channel-fields/components/aiDisclosure'
import type { DraftPreview } from '~publish/legacy/composer/composer/components/post-preview/previews/types'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import { EditorStateProxy } from '~publish/legacy/composer/composer/entities/EditorStateProxy'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'
import type { ComposerProfile } from '~publish/legacy/composer/composer/stores/types'
import { SERVICE_FACEBOOK, SERVICE_LINKEDIN } from '~publish/legacy/constants'

import {
  useChannelVariantReadsActive,
  useDraftView,
  useEditorStateEnabled,
} from '../../store/bridge'
import { useEditorForDraft } from '../../store/editorSlice/hooks'
import { useChannels } from '../../store/hooks/useChannels'
import { ChannelPostPreview, getProfilePreview } from '../ChannelPostPreview'

import {
  PostPreviewChannelSection,
  PostPreviewsPanelView,
} from './PostPreviewsPanelView'

import type { JSX } from 'react'

export const PostPreviewSidePanel = ({
  open,
  enabledComposer,
  draft,
}: {
  open: boolean
  enabledComposer: string | null
  draft?: Draft
}): JSX.Element => {
  // Check if the draft is empty or if the whole thread has no content
  // (text or media) across any of its posts.
  const isEmpty = draft?.hasThread()
    ? !draft.hasThreadContent()
    : draft?.isEmpty()

  // In omni mode with no channels selected, OmniPreviews renders nothing
  // (no profiles to iterate). Show the empty state placeholder instead.
  const isOmniWithNoChannels =
    enabledComposer === 'omni' && AppStore.getSelectedProfiles().length === 0

  return (
    <PostPreviewsPanelView
      open={open}
      key={`post-preview-side-panel-${enabledComposer || 'no-channel'}`}
      heading={
        isService(enabledComposer)
          ? `${getChannelName(enabledComposer)} Preview`
          : 'Post Previews'
      }
      isEmpty={!enabledComposer || !draft || isEmpty || isOmniWithNoChannels}
    >
      {draft &&
        (enabledComposer === 'omni' ? (
          <OmniPreviews draft={draft} />
        ) : (
          <ChannelPreview service={enabledComposer ?? ''} draft={draft} />
        ))}
    </PostPreviewsPanelView>
  )
}

const OmniPreviews = ({ draft }: { draft: Draft }): JSX.Element => {
  const selectedProfiles = AppStore.getSelectedProfiles()

  return (
    <>
      {selectedProfiles.map((profile) => {
        const service = profile.serviceName
        if (!isService(service)) {
          return null
        }

        return (
          <PostPreviewChannelSection
            key={`omni-preview-${profile.id}`}
            service={service}
            label={getChannelName(service)}
          >
            <ChannelPreview service={service} profile={profile} draft={draft} />
          </PostPreviewChannelSection>
        )
      })}
    </>
  )
}

const ChannelPreview = ({
  service,
  profile: maybeProfile,
  draft,
}: {
  service: string
  profile?: ComposerProfile
  draft?: Draft
}): JSX.Element | null => {
  // When rendered per-channel from OmniPreviews, the caller passes the
  // specific profile so each preview reflects its own channel. The fallback
  // covers the single-service case where the caller only has a service name.
  const profile =
    maybeProfile ?? AppStore.getSelectedProfilesForService(service)?.[0]
  const { channels } = useChannels({ skip: false })
  const view = useDraftView(profile?.id ?? '')
  const channelVariantReads = useChannelVariantReadsActive()
  const editorStateEnabled = useEditorStateEnabled()
  const mirroredEditor = useEditorForDraft(draft?.id ?? '')
  const channel = profile ? channels[profile.id] : undefined

  if (!service || !profile || !draft) {
    return null
  }

  const channelName = channel ? getChannelNameForDisplay(channel) : undefined

  const profilePreview = getProfilePreview(profile, channelName)

  const isOmniMode = AppStore.getIsOmniboxEnabled() === true

  const draftPreview: DraftPreview = {
    ...draft,
    isEmpty: draft.isEmpty(),
    // The seam serves omni-mode text (the shared omni content) unconditionally,
    // and serves per-channel text in customize mode when channel-variant
    // reads are active; otherwise customize-mode previews read the Flux draft
    // directly.
    text: isOmniMode || channelVariantReads ? view.text : draft.text,
    hasThread: draft.hasThread(),
    activeThreadId: ComposerStore.getActiveThreadId(),
    isAiGenerated: canDiscloseAiContent(draft) && getIsAiGenerated(draft),
    entities:
      profile.service?.name === SERVICE_FACEBOOK
        ? EditorStateProxy.getFacebookAutocompleteEntities(
            editorStateEnabled
              ? (mirroredEditor ?? draft.editorState)
              : draft.editorState,
          )
        : null,

    annotations:
      profile.service?.name === SERVICE_LINKEDIN
        ? EditorStateProxy.getAllLinkedinAnnotations(
            editorStateEnabled
              ? (mirroredEditor ?? draft.editorState)
              : draft.editorState,
          )
        : [],
  }

  return (
    <ChannelPostPreview
      service={service}
      draft={draftPreview}
      profile={profilePreview}
    />
  )
}
